import { useEffect, useRef } from 'react';

const palette = {
  dark: [[143, 182, 216], [26, 149, 211], [183, 169, 150]],
  light: [[73, 108, 137], [26, 149, 211], [183, 169, 150]],
};

export default function AmbientField({ tone = 'dark', variant = 'signal' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d', { alpha: true });
    let raf = 0;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let pointer = { x: .62, y: .42 };
    const quiet = variant === 'quiet';
    const count = quiet ? 18 : variant === 'network' ? 42 : 32;
    const particles = Array.from({ length: count }, (_, i) => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - .5) * (quiet ? .00006 : .00013),
      vy: (Math.random() - .5) * (quiet ? .00006 : .00013),
      r: 1 + Math.random() * 2.2,
      phase: i * .63,
    }));

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const onPointer = (event) => {
      pointer = { x: event.clientX / window.innerWidth, y: event.clientY / window.innerHeight };
    };
    const colors = palette[tone];
    const draw = (time) => {
      ctx.clearRect(0, 0, width, height);
      const opacity = quiet ? .16 : .34;
      particles.forEach((p, index) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -.05) p.x = 1.05;
        if (p.x > 1.05) p.x = -.05;
        if (p.y < -.05) p.y = 1.05;
        if (p.y > 1.05) p.y = -.05;
        const px = p.x * width + (pointer.x - .5) * (index % 3) * 4;
        const py = p.y * height + (pointer.y - .5) * (index % 4) * 4;
        ctx.beginPath();
        const [r, g, b] = colors[index % colors.length];
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        ctx.arc(px, py, p.r + Math.sin(time * .001 + p.phase) * .6, 0, Math.PI * 2);
        ctx.fill();
      });
      if (!quiet) {
        ctx.lineWidth = 1;
        for (let i = 0; i < particles.length; i += 1) {
          for (let j = i + 1; j < particles.length; j += 1) {
            const a = particles[i];
            const b = particles[j];
            const dx = (a.x - b.x) * width;
            const dy = (a.y - b.y) * height;
            const distance = Math.hypot(dx, dy);
            if (distance < 150) {
              ctx.strokeStyle = tone === 'dark'
                ? `rgba(143,182,216,${(1 - distance / 150) * .08})`
                : `rgba(73,108,137,${(1 - distance / 150) * .06})`;
              ctx.beginPath();
              ctx.moveTo(a.x * width, a.y * height);
              ctx.lineTo(b.x * width, b.y * height);
              ctx.stroke();
            }
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onPointer, { passive: true });
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointer);
    };
  }, [tone, variant]);

  return <canvas ref={canvasRef} className={`ambient-field ambient-${variant}`} aria-hidden="true" />;
}
